/* eslint-disable @typescript-eslint/no-extraneous-class */
interface AgencyTokenData {
  token: string;
  expiresAt: number;
  agencyId: string;
  refreshToken?: string;
  tokenType?: string;
  issuedAt?: number;
}

interface AgencyData {
  id: string;
  name: string;
  description: string;
  contactEmail: string;
  isActive: boolean;
  role: string;
  permissions: string[];
  parentAgencyId?: string; // Support hierarchical structure
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

class AgencyTokenManager {
  private static readonly AGENCY_TOKEN_KEY = 'insuracentral_agency_token';
  private static readonly AGENCY_DATA_KEY = 'insuracentral_current_agency';

  static getAgencyToken(): string | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const tokenData = sessionStorage.getItem(this.AGENCY_TOKEN_KEY);
      if (!tokenData) return null;
      
      const parsed: AgencyTokenData = JSON.parse(tokenData);
      
      // Check if token is expired
      if (Date.now() > parsed.expiresAt) {
        this.clearAgencyToken();
        return null;
      }
      
      return parsed.token;
    } catch (error) {

      return null;
    }
  }

  static setAgencyToken(
    token: string, 
    agencyId: string, 
    options: {
      expiresInHours?: number;
      refreshToken?: string;
      tokenType?: string;
      expiresAt?: string | number;
    } = {}
  ): void {
    if (typeof window === 'undefined') return;
    
    const { 
      expiresInHours = 24, 
      refreshToken, 
      tokenType = 'Bearer',
      expiresAt: providedExpiresAt 
    } = options;
    
    // Calculate expiration time
    let expiresAt: number;
    if (providedExpiresAt) {
      expiresAt = typeof providedExpiresAt === 'string' 
        ? new Date(providedExpiresAt).getTime()
        : providedExpiresAt;
    } else {
      expiresAt = Date.now() + (expiresInHours * 60 * 60 * 1000);
    }
    
    const tokenData: AgencyTokenData = {
      token,
      expiresAt,
      agencyId,
      refreshToken,
      tokenType,
      issuedAt: Date.now(),
    };
    
    try {
      sessionStorage.setItem(this.AGENCY_TOKEN_KEY, JSON.stringify(tokenData));
    } catch (error) {

    }
  }

  static clearAgencyToken(): void {
    if (typeof window === 'undefined') return;

    try {
      sessionStorage.removeItem(this.AGENCY_TOKEN_KEY);
      sessionStorage.removeItem(this.AGENCY_DATA_KEY);
      
      // Also clear user context to force re-sync
      sessionStorage.removeItem('insuracentral_user_context');
      
      // Notify that agency context was cleared
      try {
        window.dispatchEvent(new CustomEvent('insuracentral-agency-change'));
      } catch {}
    } catch (error) {

    }
  }

  static isAgencyTokenValid(): boolean {
    return this.getAgencyToken() !== null;
  }

  static getCurrentAgencyId(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      // PRIORITY 1: Get from current agency data (updated when user switches agencies)
      const agencyData = sessionStorage.getItem(this.AGENCY_DATA_KEY);
      if (agencyData) {
        const parsed: any = JSON.parse(agencyData);
        // Handle both flat structure (AgencyData) and nested structure (from userContext)
        if (parsed.id) {
          return parsed.id; // Flat structure: { id, name, ... }
        } else if (parsed.agency?.id) {
          return parsed.agency.id; // Nested structure: { agency: { id, ... }, role, ... }
        }
      }

      // PRIORITY 2: Fallback to agency token if agency data doesn't exist
      const tokenData = sessionStorage.getItem(this.AGENCY_TOKEN_KEY);
      if (tokenData) {
        const parsed: AgencyTokenData = JSON.parse(tokenData);
        return parsed.agencyId;
      }

      return null;
    } catch {
      return null;
    }
  }

  static getAgencyTokenData(): AgencyTokenData | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const tokenData = sessionStorage.getItem(this.AGENCY_TOKEN_KEY);
      if (!tokenData) return null;
      
      const parsed: AgencyTokenData = JSON.parse(tokenData);
      
      // Check if token is expired
      if (Date.now() > parsed.expiresAt) {
        this.clearAgencyToken();
        return null;
      }
      
      return parsed;
    } catch (error) {

      return null;
    }
  }

  static setCurrentAgency(agencyData: AgencyData): void {
    if (typeof window === 'undefined') return;

    try {
      const existingData = sessionStorage.getItem(this.AGENCY_DATA_KEY);
      const existing = existingData ? JSON.parse(existingData) : null;

      // CRITICAL FIX: Check if agency is actually changing
      const isActuallyChanging = !existing || existing.id !== agencyData.id;

      sessionStorage.setItem(this.AGENCY_DATA_KEY, JSON.stringify(agencyData));

      // CRITICAL FIX: Only dispatch event if agency actually changed
      // This prevents infinite loops where every profile fetch triggers query invalidations
      if (isActuallyChanging) {
        try {
          window.dispatchEvent(new CustomEvent('insuracentral-agency-change'));
        } catch {}
      }
    } catch (error) {

    }
  }

  static getCurrentAgency(): AgencyData | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const agencyData = sessionStorage.getItem(this.AGENCY_DATA_KEY);
      if (!agencyData) return null;
      
      return JSON.parse(agencyData) as AgencyData;
    } catch (error) {

      return null;
    }
  }

  static isTokenExpiringSoon(minutesThreshold = 5): boolean {
    const tokenData = this.getAgencyTokenData();
    if (!tokenData) return true;
    
    const thresholdTime = Date.now() + (minutesThreshold * 60 * 1000);
    return tokenData.expiresAt <= thresholdTime;
  }

  static getTokenExpirationTime(): Date | null {
    const tokenData = this.getAgencyTokenData();
    if (!tokenData) return null;
    
    return new Date(tokenData.expiresAt);
  }

  static refreshAgencyToken(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const tokenData = this.getAgencyTokenData();
        if (!tokenData?.refreshToken) {
          reject(new Error('No refresh token available'));
          return;
        }

        // Import the API service dynamically to avoid circular dependencies
        const apiModule = await import('./api');
        const api = apiModule.default || apiModule;
        const { endpoint } = await import('./endpoints');
        const { getApiUrl } = await import('./config');
        const { handleApiCall } = await import('../utils/apiResponseHandler');

        const response = await handleApiCall(async () => {
          return await api.post({
            url: getApiUrl(endpoint.auth.postRefreshToken),
            body: { refreshToken: tokenData.refreshToken },
            auth: false,
          });
        });

        const maybe = response as { data?: unknown } | unknown;
        const raw = (maybe as { data?: unknown })?.data ?? maybe;
        const obj = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};

        const accessToken = typeof obj.accessToken === 'string' ? obj.accessToken : undefined;
        const newRefreshToken = typeof obj.refreshToken === 'string' ? obj.refreshToken : undefined;
        const tokenType = typeof obj.tokenType === 'string' ? obj.tokenType : undefined;
        const expiresAt = (obj.expiresAt as string | number | undefined);

        if (accessToken) {
          // Update the agency token with new data
          this.setAgencyToken(accessToken, tokenData.agencyId, {
            refreshToken: newRefreshToken || tokenData.refreshToken,
            expiresAt,
            tokenType: tokenType || tokenData.tokenType,
          });
          
          resolve(true);
        } else {
          reject(new Error('No access token in refresh response'));
        }
      } catch (error) {

        this.clearAgencyToken();
        reject(error);
      }
    });
  }

  static validateAgencyAccess(requiredPermissions: string[] = []): boolean {
    const agencyData = this.getCurrentAgency();
    const tokenData = this.getAgencyTokenData();
    
    if (!agencyData || !tokenData) return false;
    
    // Check if user has required permissions
    if (requiredPermissions.length > 0) {
      return requiredPermissions.every(permission => 
        agencyData.permissions.includes(permission)
      );
    }
    
    return true;
  }
}

export default AgencyTokenManager; 